/**
 * joinRequests — data layer shared by the Join requests page, the sidebar
 * count badge and the "N pending requests" link on Platform › Apps.
 *
 * An app with `apps.require_approval` turns every self-signup (register,
 * Google, Apple) into a membership with status 'requested'; the account can
 * sign in but sees nothing until an approver decides
 * (gateway/src/core/app_membership/requests.ts). Memory Pages has its own
 * approval screen; Vault has none, so the platform console must reach every
 * app's requests (Erwin, 2026-10-19: "I need to be able to approve any
 * registrations").
 *
 * The gateway has no cross-app list, and a platform admin is an approver of
 * every app, so the console fans out the same way ApprovalsBell does for the
 * roadmap: GET /v1/admin/apps → for each app with requireApproval,
 * GET /v1/app/:appId/membership/requests (requests + members).
 *
 *   POST /v1/app/:appId/membership/requests/:userId/approve   { linkTo?: userId }
 *   POST /v1/app/:appId/membership/requests/:userId/deny
 *
 * Everything here is router-free and React-light (one hook) so the tests can
 * drive it with a stubbed fetch.
 */
import { useCallback, useEffect, useState } from 'react';
import { NexusAdminApiError } from '../api.js';
/** Window event the page fires after a decision so the sidebar badge refreshes at once. */
export const JOIN_REQUESTS_CHANGED = 'nexus:join-requests-changed';
const RELAY_RE = /@privaterelay\.appleid\.com$/i;
/** Apple "Hide My Email" relay address — can never match an existing member's email. */
export function isRelayEmail(email) {
    return !!email && RELAY_RE.test(email);
}
/** The apps whose requests the console must show: require approval and are not archived. */
export function appsNeedingApproval(apps) {
    return apps.filter((a) => a.requireApproval === true && a.status !== 'archived');
}
const PROVIDER_LABEL = { apple: 'Apple', google: 'Google' };
/** "Apple (Hide My Email)", "Google", "Email + password", "Apple, Google". */
export function signInLabel(r) {
    const names = [...new Set(r.providers.map((p) => p.provider))].map((p) => PROVIDER_LABEL[p] ?? p);
    if (names.length === 0)
        return 'Email + password';
    const relay = r.providers.some((p) => p.provider === 'apple' && (isRelayEmail(p.email) || isRelayEmail(r.email)));
    return names.join(', ') + (relay ? ' (Hide My Email)' : '');
}
/** Waiting requests first (newest on top), then the recently denied ones. */
export function sortRows(rows) {
    return [...rows].sort((a, b) => {
        if (a.status !== b.status)
            return a.status === 'requested' ? -1 : 1;
        return b.requestedAt.localeCompare(a.requestedAt);
    });
}
export function flattenJoinRequests(app, res) {
    const approvers = res.approvers === 'members' ? 'members' : 'admins';
    return (res.requests ?? []).map((r) => ({
        ...r,
        providers: r.providers ?? [],
        appId: app.appId,
        appName: app.displayName || app.appId,
        approvers,
        members: res.members ?? [],
    }));
}
export function pendingCount(rows) {
    return rows.filter((r) => r.status === 'requested').length;
}
/** Everyone's requests, across the apps that require approval. */
export async function loadJoinRequests(fetcher, onlyApp) {
    const all = (await fetcher('/v1/admin/apps'))?.apps ?? [];
    const apps = appsNeedingApproval(all).filter((a) => !onlyApp || a.appId === onlyApp);
    const errors = [];
    const perApp = await Promise.all(apps.map(async (a) => {
        try {
            const res = await fetcher(`/v1/app/${encodeURIComponent(a.appId)}/membership/requests`);
            return flattenJoinRequests(a, res);
        }
        catch (e) {
            const message = e instanceof NexusAdminApiError ? `${e.status} ${e.message}` : e instanceof Error ? e.message : String(e);
            errors.push({ appId: a.appId, message });
            return [];
        }
    }));
    return { rows: sortRows(perApp.flat()), apps, errors };
}
/** Approve, deny, or approve-as-existing-member (linkTo) one request. */
export async function decideJoinRequest(fetcher, appId, userId, d) {
    const path = `/v1/app/${encodeURIComponent(appId)}/membership/requests/${encodeURIComponent(userId)}/${d.verb}`;
    const json = d.verb === 'approve' && d.linkTo ? { linkTo: d.linkTo } : {};
    const r = await fetcher(path, { method: 'POST', json });
    try {
        if (typeof window !== 'undefined')
            window.dispatchEvent(new window.Event(JOIN_REQUESTS_CHANGED));
    }
    catch { /* the badge is a convenience; the decision already happened */ }
    return r;
}
export const JOIN_REQUESTS_POLL_MS = 60_000;
/**
 * Pending-request count for the sidebar badge. Polls every 60 s, refreshes
 * when the tab regains focus and when a decision is made anywhere in the
 * console (JOIN_REQUESTS_CHANGED). `null` until the first answer; errors keep
 * the last value — a badge must never block the nav.
 */
export function useJoinRequestCount(fetcher, pollMs = JOIN_REQUESTS_POLL_MS) {
    const [count, setCount] = useState(null);
    const load = useCallback(async () => {
        try {
            const data = await loadJoinRequests(fetcher);
            setCount(pendingCount(data.rows));
        }
        catch { /* keep what we had */ }
    }, [fetcher]);
    useEffect(() => {
        let alive = true;
        const run = () => { if (alive)
            void load(); };
        run();
        const t = setInterval(run, pollMs);
        const onVisible = () => { if (typeof document === 'undefined' || document.visibilityState === 'visible')
            run(); };
        if (typeof window !== 'undefined') {
            window.addEventListener('focus', run);
            window.addEventListener(JOIN_REQUESTS_CHANGED, run);
            document.addEventListener('visibilitychange', onVisible);
        }
        return () => {
            alive = false;
            clearInterval(t);
            if (typeof window !== 'undefined') {
                window.removeEventListener('focus', run);
                window.removeEventListener(JOIN_REQUESTS_CHANGED, run);
                document.removeEventListener('visibilitychange', onVisible);
            }
        };
    }, [load, pollMs]);
    return count;
}
//# sourceMappingURL=joinRequests.js.map